import { normalizeOperationsRealtimeMessage } from '@/lib/websocket/operationsRealtime';

type UnknownRecord = Record<string, unknown>;

export interface TeamMemberOperationsRealtimeMatchOptions {
  data: unknown;
  teamMemberUserId?: number | null;
  relevantAppointmentIds?: Iterable<string | number | null | undefined>;
}

export function isTeamMemberOperationsRefreshEvent(event: string): boolean {
  return event === 'appointment_status_changed'
    || event === 'operations_refresh'
    || event.startsWith('work_item_')
    || event.includes('workflow');
}

export function getTeamMemberRealtimeTargetUserId(message: UnknownRecord): number | null {
  const payload = message.payload && typeof message.payload === 'object' && !Array.isArray(message.payload)
    ? (message.payload as UnknownRecord)
    : {};

  const candidateUserId = Number(
    payload.target_user_id
    ?? payload.user_id
    ?? payload.assigned_to
    ?? message.actor_user_id,
  );

  return Number.isFinite(candidateUserId) ? candidateUserId : null;
}

export function getRealtimeAppointmentIds(message: UnknownRecord): string[] {
  const payload = message.payload && typeof message.payload === 'object' && !Array.isArray(message.payload)
    ? (message.payload as UnknownRecord)
    : {};

  const appointmentIds = new Set<string>();
  const candidates = [
    message.appointment_id,
    message.work_item_id,
    message.entity_id,
    payload.appointment_id,
    payload.work_item_id,
    payload.entity_id,
  ];

  candidates.forEach((value) => {
    if (value != null && String(value).trim()) {
      appointmentIds.add(String(value));
    }
  });

  const groupedIds = Array.isArray(message.appointment_ids)
    ? message.appointment_ids
    : Array.isArray(payload.appointment_ids)
      ? payload.appointment_ids
      : [];

  groupedIds.forEach((value) => {
    if (value != null && String(value).trim()) {
      appointmentIds.add(String(value));
    }
  });

  return Array.from(appointmentIds);
}

export function matchesTeamMemberOperationsRealtime(options: TeamMemberOperationsRealtimeMatchOptions) {
  const normalized = normalizeOperationsRealtimeMessage(options.data);
  if (!normalized || !isTeamMemberOperationsRefreshEvent(normalized.event)) {
    return null;
  }

  const targetUserId = getTeamMemberRealtimeTargetUserId(normalized as unknown as UnknownRecord);
  const appointmentIds = getRealtimeAppointmentIds(normalized as unknown as UnknownRecord);
  const relevantAppointmentIds = new Set(
    Array.from(options.relevantAppointmentIds ?? [])
      .filter((value): value is string | number => value != null && String(value).trim().length > 0)
      .map((value) => String(value)),
  );

  const matchesUser = Number.isFinite(options.teamMemberUserId)
    && targetUserId === options.teamMemberUserId;
  const matchesAppointment = appointmentIds.some((appointmentId) => relevantAppointmentIds.has(String(appointmentId)));

  return {
    normalized,
    matchesUser,
    matchesAppointment,
    appointmentIds,
    targetUserId,
  };
}

export function matchesAppointmentRealtimeMessage(data: unknown, workItemId?: string | number | null): boolean {
  if (workItemId == null || String(workItemId).trim().length === 0) {
    return false;
  }

  const match = matchesTeamMemberOperationsRealtime({
    data,
    relevantAppointmentIds: [workItemId],
  });

  return Boolean(match?.matchesAppointment);
}
